import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { createLoginFormGroup } from '../../shared/utils/login-form.util';
import { LoginLayoutComponent } from '../../components/login-layout/login-layout.component';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { ApiService } from '../../services/api.service';
import { LoggerService } from '../../services/logger.service';
import { UserRole } from '../../shared/types/role.interface';

/**
 * TunnelLoginComponent provides a login UI and logic for "tunnel" users, handling form validation, authentication via an API, storing session data in localStorage, and redirecting authenticated tunnel users to the tunnel area.
 * @component
 * @selector app-tunnel-login
 * @example
 *   <app-tunnel-login></app-tunnel-login>
 * @input none - This component does not accept any @Input properties; it manages its own internal form state.
 * @output none - This component does not emit any @Output events; navigation and state changes are handled internally (Router and localStorage).
 *
 * Purpose:
 *   - Present a login form (email and password) for tunnel users.
 *   - Validate the form and submit credentials to the backend via ApiService.
 *   - Restrict access to users with the UserRole.TUNNEL role.
 *   - Persist authenticated tunnel session data (token, role, location/shop identifiers, email, branch, and timestamp) to localStorage under the key "tunnelData".
 *   - Redirect authenticated tunnel users to the /tunnel route.
 *
 * Key properties:
 *   - isSubmitting: boolean - Tracks whether a login request is in progress to disable the form and show loading state.
 *   - emailError: string - Holds the current email-related error message for display.
 *   - passwordError: string - Holds the current password/credential-related error message for display.
 *   - showPassword: boolean - Toggles visibility of the password input.
 *   - loginForm: FormGroup - Reactive form group created by createLoginFormGroup() containing email and password controls.
 *
 * Key methods:
 *   - togglePasswordVisibility(): void
 *       Toggles the showPassword flag to show/hide the password input in the template.
 *
 *   - login(): void
 *       Validates the form and, if valid:
 *         - Sets isSubmitting and clears previous errors.
 *         - Sends credentials to ApiService.postWithoutAuth('location/login', payload).
 *         - On success:
 *             - Verifies the returned role is UserRole.TUNNEL; otherwise rejects access.
 *             - Extracts location and other metadata to assemble tunnelData and stores it in localStorage.
 *             - Navigates to the /tunnel route.
 *         - On failure or error:
 *             - Clears isSubmitting and sets appropriate passwordError messages.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): void
 *       - Runs on component initialization and checks localStorage for existing valid tunnelData.
 *       - If valid data with a token and role UserRole.TUNNEL is present, immediately navigates to /tunnel.
 *       - If stored data is corrupt JSON, it is removed from localStorage.
 *
 * Dependencies:
 *   - Router: used to navigate to the /tunnel route on successful authentication or when an existing tunnel session is found.
 *   - ApiService: used to post login credentials to the backend (postWithoutAuth).
 *
 * Notes:
 *   - All network responses and errors are handled to provide user-friendly error messages.
 *   - The component is standalone and imports CommonModule, LoginLayoutComponent, FormsModule, ReactiveFormsModule, and ErrorMessageComponent for template composition and form handling.
 */
@Component({
  selector: 'app-tunnel-login',
  standalone: true,
  imports: [
    CommonModule,
    LoginLayoutComponent,
    FormsModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
  ],
  templateUrl: './tunnel-login.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tunnel-login.component.scss'],
})
export class TunnelLoginComponent implements OnInit {
  private readonly loginLogSource = 'TunnelLogin';

  isSubmitting = false;
  emailError = '';
  passwordError = '';
  showPassword = false;

  loginForm = createLoginFormGroup();

  constructor(
    private router: Router,
    private apiService: ApiService,
    private logger: LoggerService
  ) {}

  private logLogin(action: string, detail?: Record<string, any>): void {
    this.logger.info(`[TunnelLogin] ${action}`, { action, ...detail }, this.loginLogSource);
  }

  private redactEmail(email: string): string {
    const t = (email || '').trim();
    if (!t.includes('@')) return '(invalid)';
    const [local, domain] = t.split('@');
    const safeLocal =
      local.length <= 1 ? '*' : `${local[0]}${'*'.repeat(Math.min(4, local.length - 1))}`;
    return `${safeLocal}@${domain}`;
  }

  /**
   * Check localStorage for tunnel data, validate it, and navigate to /tunnel if a valid tunnel token exists; clears invalid data on parse errors.
   * @example
   * ngOnInit()
   * void
   * @returns {void} Performs client-side redirect when a valid tunnel session is present; has no return value.
   */
  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      const tunnelDataStr = localStorage.getItem('tunnelData');
      if (tunnelDataStr) {
        try {
          const tunnelData = JSON.parse(tunnelDataStr);
          if (tunnelData?.token && tunnelData?.role === UserRole.TUNNEL) {
            this.logLogin('session.redirectAlreadyLoggedIn', {
              hasLocationId: !!tunnelData?.locationId,
            });
            this.router.navigate(['/tunnel']);
            return;
          }
        } catch {
          this.logLogin('session.invalidStorageCleared');
          localStorage.removeItem('tunnelData');
        }
      }
    }
    this.logLogin('page.ready');
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * Attempt to authenticate a tunnel user from the component's login form, store tunnel data on success, and navigate to the tunnel route.
   * @example
   * login()
   * undefined
   * @returns {void} No return value; updates component state, persists tunnel data to localStorage, and navigates on successful login.
   **/
  login(): void {
    if (this.loginForm.invalid) {
      this.logLogin('submit.validationFailed', {
        emailInvalid: !!this.loginForm.controls['email'].errors,
        passwordInvalid: !!this.loginForm.controls['password'].errors,
      });
      return;
    }

    this.isSubmitting = true;
    this.emailError = '';
    this.passwordError = '';

    const payload = {
      email: this.loginForm.value.email || '',
      password: this.loginForm.value.password || '',
    };

    this.logLogin('submit.start', {
      emailRedacted: this.redactEmail(payload.email),
    });

    this.apiService.postWithoutAuth('location/login', payload).subscribe({
      next: (response: any) => {
        if (response.status === true && response.data) {
          const role = response.data.role;

          if (role !== UserRole.TUNNEL) {
            this.isSubmitting = false;
            this.passwordError = 'Access denied. Only tunnel accounts can log in here.';
            this.logLogin('api.roleDenied', {
              role,
              emailRedacted: this.redactEmail(payload.email),
            });
            return;
          }

          const location = response.data.location || {};

          const tunnelData = {
            token: response.data.token,
            role: UserRole.TUNNEL,
            locationId: location.id || '',
            shopId: location.company_id || location.shop_id || '',
            email: location.email || payload.email,
            branch: location.branch || '',
            date: new Date(),
          };

          localStorage.setItem('tunnelData', JSON.stringify(tunnelData));

          this.logLogin('api.success', {
            emailRedacted: this.redactEmail(payload.email),
            locationId: tunnelData.locationId || null,
            shopId: tunnelData.shopId || null,
            branch: tunnelData.branch || null,
          });

          this.isSubmitting = false;
          this.router.navigate(['/tunnel']);
        } else {
          this.isSubmitting = false;
          this.passwordError = response.message || 'Login failed. Please try again.';
          this.logLogin('api.responseNotOk', {
            message: response.message ?? null,
            status: response.status,
          });
        }
      },
      error: (err: any) => {
        this.isSubmitting = false;
        let errorMessage = 'Invalid credentials. Please try again.';

        if (err?.status === 0) {
          errorMessage = 'Network error. Please check your internet connection or try again later.';
        } else if (err?.error?.message) {
          errorMessage = err.error.message;
        } else if (typeof err?.error === 'string') {
          errorMessage = err.error;
        }

        this.passwordError = errorMessage;
        this.logLogin('api.httpError', {
          httpStatus: err?.status,
          message: errorMessage,
          emailRedacted: this.redactEmail(payload.email),
        });
      },
    });
  }
}
